import React, { useRef, useState } from 'react';
import { HardDriveUpload, Image as ImageIcon, Loader2, X } from 'lucide-react';
import { compressEquipImage } from '../utils/compressEquipImage';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif';

export default function PermEquipPhotoModal({
  open,
  codigo,
  produtoNome,
  mode = 'upload',
  viewUrl,
  onClose,
  onUpload,
}) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const resetFile = () => {
    setFile(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (busy) return;
    resetFile();
    onClose?.();
  };

  const pickFile = async (raw) => {
    if (!raw || busy) return;
    setError('');
    try {
      const compressed = await compressEquipImage(raw);
      setFile(compressed);
    } catch (e) {
      setError(e.message || 'Arquivo inválido.');
      resetFile();
    }
  };

  const handleSubmit = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError('');
    try {
      await onUpload?.(file);
      resetFile();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Erro ao enviar imagem.');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'view') {
    return (
      <div className="perm-verve-modal-overlay perm-import-modal-overlay" role="presentation" onClick={handleClose}>
        <div
          className="perm-verve-modal perm-import-modal perm-equip-photo-modal perm-equip-photo-modal--view"
          role="dialog"
          aria-modal="true"
          aria-labelledby="perm-equip-photo-view-title"
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="perm-import-modal-close" onClick={handleClose} aria-label="Fechar">
            <X size={18} />
          </button>
          <div className="perm-import-modal-head">
            <ImageIcon size={28} strokeWidth={1.75} className="perm-import-modal-ico" aria-hidden />
            <h2 id="perm-equip-photo-view-title" className="perm-import-modal-title">
              Imagem do equipamento {codigo}
            </h2>
          </div>
          {produtoNome ? <p className="perm-equip-photo-sub">{produtoNome}</p> : null}
          <div className="perm-equip-photo-preview">
            {viewUrl ? <img src={viewUrl} alt={`Equipamento ${codigo}`} /> : <p className="perm-empty">Imagem indisponível.</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="perm-verve-modal-overlay perm-import-modal-overlay" role="presentation" onClick={handleClose}>
      <div
        className="perm-verve-modal perm-import-modal perm-equip-photo-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="perm-equip-photo-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="perm-import-modal-close"
          onClick={handleClose}
          disabled={busy}
          aria-label="Fechar"
        >
          <X size={18} />
        </button>
        <div className="perm-import-modal-head">
          <HardDriveUpload size={28} strokeWidth={1.75} className="perm-import-modal-ico" aria-hidden />
          <h2 id="perm-equip-photo-title" className="perm-import-modal-title">
            Definir foto do equipamento
          </h2>
        </div>
        <p className="perm-import-intro">
          Código <strong>{codigo}</strong>
          {produtoNome ? (
            <>
              {' '}
              — <span className="perm-equip-photo-prod">{produtoNome}</span>
            </>
          ) : null}
          . Envie uma imagem (JPG, PNG, WebP ou GIF), máximo <strong>5MB</strong>. Será compactada
          automaticamente.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          className="perm-import-file-input"
          aria-hidden
          tabIndex={-1}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) pickFile(f);
          }}
        />
        <div
          className={`perm-import-drop ${dragOver ? 'perm-import-drop--active' : ''} ${file ? 'perm-import-drop--has-file' : ''}`}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) pickFile(f);
          }}
        >
          {file ? (
            <div className="perm-import-file-selected">
              <HardDriveUpload size={22} strokeWidth={1.75} className="perm-import-file-ico" aria-hidden />
              <span className="perm-import-file-name" title={file.name}>
                {file.name}
              </span>
              <button
                type="button"
                className="perm-import-file-remove"
                onClick={(ev) => {
                  ev.stopPropagation();
                  resetFile();
                }}
                disabled={busy}
                aria-label="Remover arquivo"
              >
                <X size={16} strokeWidth={2.25} />
              </button>
              <p className="perm-import-drop-hint perm-import-drop-hint--file">
                Apenas uma imagem por equipamento. Remova para escolher outra.
              </p>
            </div>
          ) : (
            <>
              <p className="perm-import-drop-title">Arraste uma imagem aqui</p>
              <p className="perm-import-drop-sub">ou</p>
              <button
                type="button"
                className="perm-import-pick-btn"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
              >
                Selecionar imagem
              </button>
              <p className="perm-import-drop-hint">Apenas um arquivo por vez.</p>
            </>
          )}
        </div>
        {error ? <p className="perm-equip-photo-error" role="alert">{error}</p> : null}
        <div className="perm-import-actions">
          <button type="button" className="perm-import-pick-btn" onClick={handleClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="perm-import-process-btn"
            onClick={handleSubmit}
            disabled={!file || busy}
          >
            {busy ? <Loader2 size={16} className="perm-inline-spin" aria-hidden /> : null}
            Enviar imagem
          </button>
        </div>
      </div>
    </div>
  );
}
